/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { ApiKey, IssuedApiKeySecret, SaveApiKey } from 'model/apiKeys';
import {
    API_KEYS_ERROR,
    API_KEYS_CLEAR_ERROR,
    API_KEYS_CLEAR_SUCCESS,
    API_KEYS_RESET,
    API_KEYS_FETCH_START,
    API_KEYS_FETCH_SUCCESS,
    API_KEYS_SAVE_START,
    API_KEYS_SAVE_SUCCESS,
    API_KEYS_ROTATE_START,
    API_KEYS_ROTATE_SUCCESS,
    API_KEYS_REVOKE_START,
    API_KEYS_REVOKE_SUCCESS,
    ApiKeysAction
} from './ApiKeysActionsTypes';
import { convertToApiKey, convertToSaveApiKeyDto } from './ApiKeysConverter';
import { ApiKeyDto } from './ApiKeysDtos';
import { showErrorThunk } from '@spike/notifications-action';

/**
 * The backend route is nested under the organization and calls the entity a
 * "project". Customer-facing it is an API Key; only the URL keeps the backend
 * wording.
 */
const apiKeysUrl = (organizationId: number): string => `/organizations/${organizationId}/projects`;

// Extract meaningful error message from API error response
const extractErrorMessage = (apiError: any): string => {
    // Handle Axios error structure
    if (apiError?.response?.data) {
        const { data } = apiError.response;

        // Check for validation errors (422 status) - direct field errors like
        // {"scopes":["contains unknown values: foo"]}
        if (typeof data === 'object' && !data.message && !data.error && !data.errors) {
            // Look for field validation errors directly on data object
            const fieldNames = Object.keys(data);
            if (fieldNames.length > 0) {
                const firstField = fieldNames[0];
                const fieldError = data[firstField];
                if (Array.isArray(fieldError) && fieldError.length > 0) {
                    return `${firstField} ${fieldError[0]}`;
                }
                if (typeof fieldError === 'string') {
                    return `${firstField} ${fieldError}`;
                }
            }
        }

        // Check for validation errors in 'errors' property
        if (data.errors) {
            if (typeof data.errors === 'object') {
                // Extract first error from validation errors object
                const firstField = Object.keys(data.errors)[0];
                const firstError = data.errors[firstField];
                if (Array.isArray(firstError)) {
                    return firstError[0];
                }
                return firstError;
            }
            return data.errors;
        }

        // Check for general error message
        if (data.message) {
            return data.message;
        }

        // Check for error field
        if (data.error) {
            return data.error;
        }
    }

    // Fallback to Axios message or generic error
    if (apiError?.message) {
        return apiError.message;
    }

    // Final fallback
    return 'An unexpected error occurred. Please try again.';
};

const error = (err: string): ApiKeysAction => {
    return {
        type: API_KEYS_ERROR,
        payload: {
            error: err
        }
    };
};

export const clearApiKeysError = (): ApiKeysAction => {
    return {
        type: API_KEYS_CLEAR_ERROR
    };
};

export const clearApiKeysSuccess = (): ApiKeysAction => {
    return {
        type: API_KEYS_CLEAR_SUCCESS
    };
};

export const resetApiKeysState = (): ApiKeysAction => {
    return {
        type: API_KEYS_RESET
    };
};

/**
 * Fetch API Keys - GET
 * Retrieves every API Key of the organization, active and revoked.
 */
export const fetchApiKeysThunk = (
    organizationId: number
): ThunkAction<Promise<Array<ApiKey>>, RootState, null, ApiKeysAction> => {
    return async dispatch => {
        dispatch({ type: API_KEYS_FETCH_START });

        try {
            const { data }: AxiosResponse<Array<ApiKeyDto>> = await ApiClient.get(apiKeysUrl(organizationId));

            const apiKeys = Array.isArray(data) ? data.map(convertToApiKey) : [];

            dispatch({
                type: API_KEYS_FETCH_SUCCESS,
                payload: { apiKeys }
            });

            return apiKeys;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};

/**
 * Create API Key - POST
 *
 * Returns the created key AND its secret. The secret is read off the raw
 * response and handed back to the caller; it is never dispatched, so it does
 * not reach the Redux store or DevTools. Hold it in component state for the
 * lifetime of the reveal modal and let it go.
 *
 * The backend returns it exactly once. There is no way to read it again.
 */
export const createApiKeyThunk = (
    organizationId: number,
    apiKey: SaveApiKey
): ThunkAction<Promise<IssuedApiKeySecret>, RootState, null, ApiKeysAction> => {
    return async dispatch => {
        dispatch({ type: API_KEYS_SAVE_START });

        const payload = convertToSaveApiKeyDto(apiKey);

        try {
            const { data }: AxiosResponse<ApiKeyDto> = await ApiClient.post(apiKeysUrl(organizationId), payload);

            const saved = convertToApiKey(data);

            dispatch({
                type: API_KEYS_SAVE_SUCCESS,
                payload: { apiKey: saved }
            });

            return { apiKey: saved, secret: data.client_secret ?? '' };
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};

/**
 * Update API Key - PATCH
 * Name, scopes, location allowlist and expiry. Credentials are untouched.
 */
export const updateApiKeyThunk = (
    organizationId: number,
    apiKeyId: number,
    apiKey: SaveApiKey
): ThunkAction<Promise<ApiKey>, RootState, null, ApiKeysAction> => {
    return async dispatch => {
        dispatch({ type: API_KEYS_SAVE_START });

        const payload = convertToSaveApiKeyDto(apiKey);

        try {
            const url = `${apiKeysUrl(organizationId)}/${apiKeyId}`;
            const { data }: AxiosResponse<ApiKeyDto> = await ApiClient.patch(url, payload);

            const saved = convertToApiKey(data);

            dispatch({
                type: API_KEYS_SAVE_SUCCESS,
                payload: { apiKey: saved }
            });

            return saved;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};

/**
 * Rotate secret - POST
 *
 * Destructive and immediate: the previous secret is revoked and every token
 * already issued from it stops working the moment this returns. The partner's
 * integration is down until the new secret is deployed. The caller must have
 * confirmed that before getting here.
 *
 * Like create, the new secret is returned and never dispatched.
 */
export const rotateApiKeySecretThunk = (
    organizationId: number,
    apiKeyId: number
): ThunkAction<Promise<IssuedApiKeySecret>, RootState, null, ApiKeysAction> => {
    return async dispatch => {
        dispatch({ type: API_KEYS_ROTATE_START });

        try {
            const url = `${apiKeysUrl(organizationId)}/${apiKeyId}/rotate_secret`;
            const { data }: AxiosResponse<ApiKeyDto> = await ApiClient.post(url);

            const rotated = convertToApiKey(data);

            dispatch({
                type: API_KEYS_ROTATE_SUCCESS,
                payload: { apiKey: rotated }
            });

            return { apiKey: rotated, secret: data.client_secret ?? '' };
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};

/**
 * Revoke API Key - DELETE
 *
 * Logical, not destructive: the backend sets `active: false` and keeps the
 * row, so the key stays in the list as inactive. Takes effect immediately.
 */
export const revokeApiKeyThunk = (
    organizationId: number,
    apiKeyId: number
): ThunkAction<Promise<void>, RootState, null, ApiKeysAction> => {
    return async dispatch => {
        dispatch({ type: API_KEYS_REVOKE_START });

        try {
            const url = `${apiKeysUrl(organizationId)}/${apiKeyId}`;
            await ApiClient.delete(url);

            dispatch({
                type: API_KEYS_REVOKE_SUCCESS,
                payload: { apiKeyId }
            });
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};
